import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import { getUserTypePreference } from '../../../../util/dataExtractors';
import { UserTypePreference } from '../../../../util/enums';
import Icons from '../../../../components/Icons/Icons';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
} from '../../../../util/urlHelpers';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={classNames(css.topbarLink, css.signupLink)}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={classNames(css.topbarLink, css.loginLink)}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const BookingsLink = ({ currentPage, notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={classNames(css.topbarLink, {
        [css.currentPage]: currentPage?.indexOf('InboxPage') === 0,
      })}
      name="InboxPage"
      params={{ tab: inboxTab }}
    >
      <FormattedMessage id="TopbarDesktop.inbox" />
      {notificationDot}
    </NamedLink>
  );
};

const MessagesLink = ({ currentPage, inboxTab }) => (
  <NamedLink
    className={classNames(css.topbarLink, {
      [css.currentPage]: currentPage?.indexOf('MessagesPage') === 0,
    })}
    name="MessagesPage"
    params={{ tab: inboxTab }}
  >
    <FormattedMessage id="TopbarDesktop.messages" />
  </NamedLink>
);

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const userType = getUserTypePreference(currentUser);
  const isSeller = userType === UserTypePreference.PRIVATE_CHEFS;
  const authorListingId = currentUser?.attributes?.profile?.publicData?.authorListingId || '';
  const title = currentUser?.attributes?.profile?.displayName;
  const slug = title ? createSlug(title) : 'profile';
  const isChefProfilePublished = currentUser?.attributes?.profile?.publicData?.isPublished;
  const chefProfileLinkProps = authorListingId
    ? {
        name: 'EditListingPage',
        params: {
          id: authorListingId,
          slug,
          type: isChefProfilePublished
            ? LISTING_PAGE_PARAM_TYPE_EDIT
            : LISTING_PAGE_PARAM_TYPE_DRAFT,
          tab: 'basic-info',
        },
      }
    : { name: 'NewListingPage' };

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      {isSeller ? (
        <MenuContent className={css.profileMenuContent}>
          {/* {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null} */}
          <MenuItem key="EditListingPage">
            <NamedLink
              {...chefProfileLinkProps}
              className={classNames(css.menuLink, currentPageClass('EditListingPage'))}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage
                id={
                  isChefProfilePublished
                    ? 'TopbarDesktop.yourChefProfile'
                    : 'TopbarDesktop.completeChefProfile'
                }
              />
            </NamedLink>{' '}
          </MenuItem>

          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      ) : (
        <MenuContent className={css.profileMenuContent}>
          {/* {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null} */}
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>{' '}
          </MenuItem>
          <MenuItem key="WishlistPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('WishlistPage'))}
              name="WishlistPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.wishlistLink" defaultMessage="Wishlist" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      )}
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    viewport,
  } = props;
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const isMobileLayout = viewport?.width && viewport?.width > 0 && viewport?.width < 768;
  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);
  const accountAreaLinksMaybe = authenticatedOnClientSide ? (
    <>
      <BookingsLink
        currentPage={currentPage}
        notificationCount={notificationCount}
        inboxTab={inboxTab}
      />
      <MessagesLink currentPage={currentPage} inboxTab={inboxTab} />
    </>
  ) : null;
  const userType = getUserTypePreference(currentUser);
  const isChef = userType === UserTypePreference.PRIVATE_CHEFS;
  const authorListingId = currentUser?.attributes?.profile?.publicData?.authorListingId || '';
  const title = currentUser?.attributes?.profile?.displayName;
  const slug = title && createSlug(title);
  const isChefProfilePublished = currentUser?.attributes?.profile?.publicData?.isPublished;
  const chefProfileLinkProps = authorListingId
    ? {
        name: 'EditListingPage',
        params: {
          id: authorListingId,
          slug,
          type: isChefProfilePublished
            ? LISTING_PAGE_PARAM_TYPE_EDIT
            : LISTING_PAGE_PARAM_TYPE_DRAFT,
          tab: 'basic-info',
        },
      }
    : { name: 'NewListingPage' };
  const chefProfileLinkMaybe =
    authenticatedOnClientSide && isChef ? (
      <NamedLink {...chefProfileLinkProps} className={css.topbarLink}>
        <FormattedMessage
          id={
            isChefProfilePublished
              ? 'TopbarDesktop.yourChefProfile'
              : 'TopbarDesktop.completeChefProfile'
          }
        />
      </NamedLink>
    ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );
  const links = [
    { text: 'Help Center', name: 'LandingPage' },
    { text: 'Guides & Reviews', name: 'LandingPage' },
    { text: 'Value Guide', name: 'LandingPage' },
    { text: 'How it works', name: 'LandingPage' },
    { text: 'FAQ’s', name: 'LandingPage' },
    { text: 'Boot blog', name: 'LandingPage' },
    { text: 'Sync shopify', name: 'LandingPage' },
  ];
  return (
    <nav className={classes}>
      <div className={css.mainTopHeader}>
        <div className={css.fixedWidthContainer}>
          <div className={css.leftNav}>
            <LinkedLogo
              className={css.logoLink}
              layout="desktop"
              alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
          </div>
          <div className={css.middleNav}>
            {isChefProfilePublished && (
              <CustomLinksMenu
                currentPage={currentPage}
                customLinks={customLinks}
                intl={intl}
                hasClientSideContentReady={
                  authenticatedOnClientSide || !isAuthenticatedOrJustHydrated
                }
                showCreateListingsLink={showCreateListingsLink}
              />
            )}
            <NamedLink name="SearchPage" className={css.topbarLink}>
              Our chefs
            </NamedLink>

            <Link to="/p/about" className={css.topbarLink}>
              About us
            </Link>

            {chefProfileLinkMaybe}

            {!isAuthenticatedOrJustHydrated ? (
              <NamedLink
                name="SignupForUserTypePage"
                params={{ userType: UserTypePreference.PRIVATE_CHEFS }}
                className={css.topbarLink}
              >
                Chef Register
              </NamedLink>
            ) : null}
          </div>

          <div className={css.rightNav}>
            {accountAreaLinksMaybe}
            {loginLinkMaybe}
            {signupLinkMaybe}
            {profileMenuMaybe}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
